import { Component, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Store } from "@ngrx/store";
import { autoArchivePatternTypes } from "src/app/lib/auto-archive";
import { AppState } from "src/app/state";
import * as AutoArchiveActions from "src/app/state/auto-archive/auto-archive.actions";
import {
    selectAutoArchiveRules,
    selectAutoArchiveLoading,
} from "src/app/state/auto-archive/auto-archive.selectors";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatNativeDateModule } from "@angular/material/core";
import { RouterModule } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { SpinnerLoaderService } from "src/app/service/spinner-loader.service";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatSortModule } from "@angular/material/sort";
import AutoArchiveRuleRow from "src/app/model/auto-archive/AutoArchiveRuleRow";
import { MatIconModule } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { RuleDialog } from "./auto-archive-dialog.component";
import { firstValueFrom, Observable } from "rxjs";

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MatPaginatorModule,
        MatTableModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatNativeDateModule,
        MatInputModule,
        MatButtonModule,
        MatSortModule,
        MatIconModule,
    ],
    providers: [HttpClient, SpinnerLoaderService],
    selector: "app-auto-archive",
    templateUrl: "./auto-archive.component.html",
    styleUrls: ["./auto-archive.component.scss"],
})
export class AutoArchiveComponent implements OnInit {
    dataSource = new MatTableDataSource<AutoArchiveRuleRow>([]);
    autoArchiveRules$: Observable<AutoArchiveRuleRow[]>;
    loading$: Observable<boolean>;
    dataTableVisibility = false;

    constructor(private store: Store<AppState>) {
        this.autoArchiveRules$ = this.store.select(selectAutoArchiveRules);
        this.loading$ = this.store.select(selectAutoArchiveLoading);
    }

    displayedColumns = [
        "id",
        "endpoint",
        "method",
        "sendingSystem",
        "sendingUnit",
        "createdAt",
        "actions",
        "expand",
    ];
    expandedElement: AutoArchiveRuleRow | null;
    readonly dialog = inject(MatDialog);

    ngOnInit(): void {
        this.store.dispatch(AutoArchiveActions.loadRules());

        this.autoArchiveRules$.subscribe((rules) => {
            this.dataSource.data = rules || [];
            this.dataTableVisibility = rules ? rules.length > 0 : false;
        });
    }

    isExpanded(element: AutoArchiveRuleRow) {
        return this.expandedElement === element;
    }

    toggle(element: AutoArchiveRuleRow) {
        this.expandedElement = this.isExpanded(element) ? null : element;
    }

    async openCreateRuleModal() {
        await firstValueFrom(
            this.dialog
                .open(RuleDialog, {
                    maxWidth: "1200px",
                    width: "90vw",
                })
                .afterClosed()
        );
    }

    async onEdit(rule: AutoArchiveRuleRow) {
        await firstValueFrom(
            this.dialog
                .open(RuleDialog, {
                    data: rule,
                    maxWidth: "1200px",
                    width: "90vw",
                })
                .afterClosed()
        );
    }

    onDelete(rule: AutoArchiveRuleRow) {
        const answer = confirm(
            `Are you sure you want to delete rule ${rule.id}?`
        );

        if (answer) {
            this.store.dispatch(AutoArchiveActions.deleteRule({ id: rule.id }));
        }
    }

    getPatternTypeLabel(patternType: string): string {
        const type = autoArchivePatternTypes.find(
            (t) => t.value === patternType
        );
        return type?.label ?? patternType;
    }
}
